import { TheiaPlatformApiContribution } from '@theia-lved-core/browser/platform/theia-platform-api-contribution';
import { isBrowserOnlyTheia } from '@theia-lved-core/browser/online-preview/environment';
import { LayoutMode, PROJECT_COMMANDS, type PROJECT_COMMANDS as ProjectCommand } from '@lved/shared-core';
import {
    ApplicationShell,
    FrontendApplicationContribution,
    KeybindingContribution,
    KeybindingRegistry
} from '@theia/core/lib/browser';
import { Command, CommandContribution, CommandRegistry } from '@theia/core/lib/common';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { inject, injectable } from '@theia/core/shared/inversify';

const EXPORT_CODE_COMMAND: Command = {
    id: 'lvgl.project.exportCode',
    label: 'Export Code'
};

const BUILD_PROJECT_COMMAND: Command = {
    id: 'lvgl.project.build',
    label: 'Build UI Project'
};

const CLEAN_AND_BUILD_PROJECT_COMMAND: Command = {
    id: 'lvgl.project.cleanAndBuild',
    label: 'Clean and Build UI Project'
};

@injectable()
export class ProjectCommandsContribution
    implements FrontendApplicationContribution, CommandContribution, KeybindingContribution
{
    @inject(TheiaPlatformApiContribution)
    protected readonly platformApiContribution!: TheiaPlatformApiContribution;

    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    protected readonly toDispose: DisposableCollection = new DisposableCollection();

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand(EXPORT_CODE_COMMAND, {
            execute: (): Promise<void> => this.sendProjectCommand(PROJECT_COMMANDS.EXPORT_CODE),
            isEnabled: (): boolean => !isBrowserOnlyTheia()
        });
        commands.registerCommand(BUILD_PROJECT_COMMAND, {
            execute: (): Promise<void> => this.sendProjectCommand(PROJECT_COMMANDS.BUILD_PROJECT),
            isEnabled: (): boolean => this.isBuildCommandEnabled()
        });
        commands.registerCommand(CLEAN_AND_BUILD_PROJECT_COMMAND, {
            execute: (): Promise<void> => this.sendProjectCommand(PROJECT_COMMANDS.CLEAN_AND_BUILD_PROJECT),
            isEnabled: (): boolean => this.isBuildCommandEnabled()
        });
    }

    registerKeybindings(keybindings: KeybindingRegistry): void {
        keybindings.registerKeybinding({
            command: EXPORT_CODE_COMMAND.id,
            keybinding: 'ctrlcmd+e'
        });
        keybindings.registerKeybinding({
            command: BUILD_PROJECT_COMMAND.id,
            keybinding: 'ctrlcmd+b'
        });
        keybindings.registerKeybinding({
            command: CLEAN_AND_BUILD_PROJECT_COMMAND.id,
            keybinding: 'shift+ctrlcmd+b'
        });
    }

    async onStart(): Promise<void> {
        await this.platformApiContribution.apiReady;
        const { addProjectCommandsListener, callbackRegistry, removeProjectCommandsListener } =
            await import('@lved/ui');
        addProjectCommandsListener();
        callbackRegistry.setCallback('saveAllUnsavedTabs', async (): Promise<void> => this.shell.saveAll());
        this.toDispose.push({
            dispose: (): void => {
                callbackRegistry.setCallback('saveAllUnsavedTabs', null);
                removeProjectCommandsListener();
            }
        });
    }

    onStop(): void {
        this.toDispose.dispose();
    }

    private isBuildCommandEnabled(): boolean {
        return !isBrowserOnlyTheia() && document.body.dataset.lvglLayout === LayoutMode.CODE;
    }

    private async sendProjectCommand(projectCommand: ProjectCommand): Promise<void> {
        await this.shell.saveAll();

        const platformApi = await this.platformApiContribution.apiReady;
        platformApi.sendGlobalEvent(projectCommand);
    }
}
